import * as React from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import { rem, darken } from 'polished';
import { TextP } from '../../Defaults/Text';
import { FlexCol, FlexBetween } from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { Input } from '../General/Input';
import { TableTd, TableRow } from '../General/Table';
import { ProductPriceCartTable } from './ProductPriceCartTable';
import { getProductDefaultInfo } from '../../../utilities/selectors';
import { StockInfo } from './StockInfo';
import { OrderNrInput } from './OrderNrInput';
import { getZemplinProductDefaultInfo } from '../selectors';

export interface Props {
  product: any;
  addToRequest: any;
  addToCart: any;
  isLoggedIn?: boolean;
  handleOrderNrChange: (goodId, value) => void;
  showPackage?: boolean;
}

function ProductTabRowTable({
  product,
  addToCart,
  addToRequest,
  isLoggedIn,
  handleOrderNrChange,
  showPackage,
}: Props) {
  const { picture, url, name, goodId } = getProductDefaultInfo(product);
  const {
    unit,
    onStock,
    price,
    priceWithoutVat,
    storesInfo,
    orderNr,
    productPackages,
  } = getZemplinProductDefaultInfo(product);
  const correctPrice = isLoggedIn ? priceWithoutVat : price;

  return (
    <>
      <ImageNameTableTd>
        <ImageWrapper to={url}>
          <Image alt={`Obrázok ku produktu ${name}`} src={picture} />
        </ImageWrapper>
        <NameInputWrapper>
          <Link to={url}>
            <Name>{name}</Name>
          </Link>
          <OrderNrInput
            goodId={goodId}
            defaultValue={orderNr}
            handleChange={handleOrderNrChange}
          />
        </NameInputWrapper>
      </ImageNameTableTd>

      <StyledTableTd>
        <StockInfo
          storesInfo={storesInfo}
          withoutExactStockCount={false}
          onStock={onStock}
          unit={unit}
          bottomMargin={false}
        />
      </StyledTableTd>
      {showPackage && (
        <PackageTableTd>{`${productPackages} ${unit}`}</PackageTableTd>
      )}
      <ProductPriceCartTable
        price={correctPrice}
        unit={unit}
        addToRequest={addToRequest}
        addToCart={addToCart}
      />
    </>
  );
}

const StyledTableTd = styled(TableTd)`
  padding: ${rem(24)} 0 ${rem(16)};
  border-bottom: ${({ theme }) => theme.borders.primary};
`;

const ImageNameTableTd = styled(StyledTableTd)`
  display: flex;
  align-items: center;
`;

const ImageWrapper = styled(Link)`
  margin-right: ${rem(24)};
`;

const PackageTableTd = styled(TableTd)`
  padding: ${rem(24)} 0 ${rem(16)};
  border-bottom: ${({ theme }) => theme.borders.primary};
  font-family: ${({ theme }) => theme.font.tertiary};
  font-size: ${rem(12)};
  line-height: ${rem(22)};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-right: ${rem(6)};
  white-space: nowrap;
`;

const Image = styled.img`
  width: ${rem(48)};
  height: ${rem(48)};
  max-width: initial;
`;

const NameInputWrapper = styled(FlexCol)`
  ${({ theme }) => theme.mediab.l1050`
      max-width: ${rem(130)};
  `}

  ${({ theme }) => theme.mediab.l925`
      max-width: ${rem(100)};
  `}
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  margin-bottom: ${rem(12)};
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.primary};
  text-align: left;
  font-family: ${({ theme }) => theme.font.secondary};
  font-weight: 300;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${rem(400)};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }

  ${({ theme }) => theme.mediab.l1300`
      max-width: ${rem(300)};
  `}

  ${({ theme }) => theme.mediab.l1150`
      max-width: ${rem(200)};
  `}
`;

const StyledTableRow = styled(TableRow)`
  padding: ${rem(24)} 0;
  border-top: ${({ theme }) => theme.borders.primary};

  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

export { ProductTabRowTable, StyledTableRow };
